import { useAuth0 } from '../../../contexts/auth0-context'
import callSecureAPI from '../../../API/callSecureAPI'
import endpoints from '../../../API/endpoints'
import { useSelector } from 'react-redux'
import callAPI from '../../../API/callSecureAPI'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { Comment, CommentsProps } from './Comments.types'
import { checkIfTableDataType } from './Comments.helpers'
import { useMemo } from 'react'
import { getCommentsDashboardQueryKey } from '../../../components/CommentsView/queries/useGetCommentsDashboardQuery'

interface UseCommentsQueriesProps extends CommentsProps {
    onPostComment?: () => void
    onRemoveComment?: (commentId: string) => void
    onUpdateComment?: (commentId: string) => void
}

const TWO_MINUTES = 1000 * 60 * 2

export const getCommentsQueryKey = (dataId: string, dataType: CommentsProps['dataType']) =>
    ['comments', dataId, dataType] as const

const getNormalizedCommentUpdates = (updates: Partial<Pick<Comment, 'action' | 'isResolved'>>) => {
    const normalized: Partial<Pick<Comment, 'action' | 'isResolved'>> = {}
    if (Object.prototype.hasOwnProperty.call(updates, 'action')) normalized.action = updates.action?.trim() ?? ''
    if (Object.prototype.hasOwnProperty.call(updates, 'isResolved')) normalized.isResolved = Boolean(updates.isResolved)
    return normalized
}

export const useInvalidateGetCommentsQuery = () => {
    const queryClient = useQueryClient()

    const invalidateGetCommentsQuery = async (
        dataId?: string,
        dataType?: CommentsProps['dataType'],
        strategyId?: string
    ) => {
        const queryKey = ['comments']
        if (dataId) queryKey.push(dataId)

        await queryClient.invalidateQueries({ queryKey, exact: false, refetchActive: true, refetchInactive: false })

        if (strategyId) {
            await queryClient.invalidateQueries({
                queryKey: getCommentsDashboardQueryKey(strategyId),
                exact: true,
                refetchActive: true,
                refetchInactive: false,
            })
        }
    }

    return { invalidateGetCommentsQuery }
}

export const useCommentsQueries = ({
    dataId,
    dataType,
    onPostComment,
    onRemoveComment,
    onUpdateComment,
}: UseCommentsQueriesProps) => {
    // @ts-expect-error store is not typed properly
    const userId = useSelector((state) => state.userRecord?._id)
    // @ts-expect-error store is not typed properly
    const strategyId = useSelector((state) => state.strategy?._id)
    // @ts-expect-error store is not typed properly
    const organizationId = useSelector((state) => state.strategy?.organizationId)

    const { getTokenSilently, isAuthenticated, isLoading } = useAuth0()
    const queryClient = useQueryClient()
    const isTableDataType = useMemo(() => checkIfTableDataType(dataType), [dataType])
    const commentsQueryKey = getCommentsQueryKey(dataId, dataType)

    const getComments = async () => {
        const token = await getTokenSilently({ audience: 'https://atlas.aesara.com' })

        if (isTableDataType) {
            const { url, method } = endpoints.getTableComments
            const response = await callSecureAPI(url + dataType + '/' + dataId, method, token)
            return response?.data || []
        }

        const { url, method } = endpoints.getDataComments
        const response = await callSecureAPI(url + dataId, method, token)
        return response?.data || []
    }

    const postComment = async (text: string) => {
        const { url, method } = endpoints.postDataComment

        const token = await getTokenSilently({ audience: 'https://atlas.aesara.com' })

        const response = await callSecureAPI(url, method, token, {
            text,
            userId,
            dataType,
            dataId,
            strategyId,
            organizationId,
        })

        if (!response?.success) throw new Error(response?.error || 'Failed to update comment')

        return response?.data as Comment
    }

    const removeComment = async (commentId: string) => {
        const { url, method } = endpoints.deleteDataComment

        const token = await getTokenSilently({ audience: 'https://atlas.aesara.com' })

        await callAPI(url + commentId, method, token)
    }

    const updateComment = async ({
        commentId,
        ...updates
    }: {
        commentId: string
        action?: string
        isResolved?: boolean
    }) => {
        const token = await getTokenSilently({ audience: 'https://atlas.aesara.com' })

        const { url, method } = endpoints.updateDataComment

        const response = await callSecureAPI(url + commentId, method, token, getNormalizedCommentUpdates(updates))

        if (!response?.success) throw new Error(response?.error || 'Failed to update comment')

        return response?.data as Partial<Comment> & Pick<Comment, '_id'>
    }

    const getCommentsQuery = useQuery<Comment[]>(commentsQueryKey, getComments, {
        enabled: !!getTokenSilently && !!isAuthenticated && !isLoading && !!dataId && !!dataType,
        staleTime: TWO_MINUTES,
        cacheTime: TWO_MINUTES,
    })

    const { invalidateGetCommentsQuery } = useInvalidateGetCommentsQuery()

    const postCommentMutation = useMutation<Comment, Error, unknown>(postComment, {
        onSuccess: async (createdComment) => {
            queryClient.setQueryData<Comment[]>(commentsQueryKey, (current) => [...(current ?? []), createdComment])
            await invalidateGetCommentsQuery(undefined, undefined, strategyId)
            onPostComment?.()
        },
    })

    const removeCommentMutation = useMutation(removeComment, {
        onSuccess: (_data, commentId) => {
            queryClient.setQueryData<Comment[]>(
                commentsQueryKey,
                (current) => current?.filter((c) => c._id !== commentId) ?? []
            )
            if (strategyId) {
                queryClient.setQueryData<any[]>(
                    getCommentsDashboardQueryKey(strategyId),
                    (current) => current?.filter((c) => c._id !== commentId) ?? []
                )
            }
        },
        onSettled: (_data, _error, commentId) => {
            onRemoveComment?.(commentId)
        },
    })

    const updateCommentMutation = useMutation<
        Partial<Comment> & Pick<Comment, '_id'>,
        Error,
        { commentId: string; action?: string; isResolved?: boolean }
    >(updateComment, {
        onSuccess: (updatedComment) => {
            queryClient.setQueryData<Comment[]>(
                commentsQueryKey,
                (current) => current?.map((c) => (c._id === updatedComment._id ? { ...c, ...updatedComment } : c)) ?? []
            )
            if (strategyId) {
                queryClient.setQueryData<any[]>(
                    getCommentsDashboardQueryKey(strategyId),
                    (current) =>
                        current?.map((c) => (c._id === updatedComment._id ? { ...c, ...updatedComment } : c)) ?? []
                )
            }
        },
        onSettled: (_data, _error, variables) => {
            onUpdateComment?.(variables.commentId)
        },
    })

    return { getCommentsQuery, postCommentMutation, removeCommentMutation, updateCommentMutation }
}
